import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad, formatoPrecio } from '@/lib/utils/formato-precio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { Action, RuleScope, ScopeCategory, ScopeProduct } from '../types/pricing-rules.types';
import { agruparMarcas, type MarcaAgrupada } from '../lib/marcas';
import { usePedidoConEspera } from '../lib/use-pedido-con-espera';

/**
 * Elegir muchos productos de una lista (Sebastián, 27-sep): en vez de buscarlos
 * de a uno, una ventana de pantalla completa con búsqueda, filtros por categoría
 * y marca, casillas y la lista de lo elegido.
 *
 * En Reglas lista lo que alcanza `scope` con la misma cuenta que el paso 2
 * (`/simular` con origen HOY): para las excepciones, el alcance de la regla; para
 * los productos puntuales, todo el catálogo. Una estrategia pasa su `fuente`
 * (plan 3, P3-D12). De a 50, que es lo que `/simular` devuelve por vez.
 */

const POR_PAGINA = 50;
const num = formatoCantidad;
const precio = formatoPrecio;

/** Lo que el selector pide a su fuente: la búsqueda y los dos desplegables. */
export interface FiltroDeLaLista {
  q?: string;
  category?: string;
  brand?: string;
}

/**
 * Una fila de la lista: lo que se ve de cada producto. Escaneo previo Nº 17: es
 * la forma de `ScopeProduct` (ya importado en este archivo), con la marca y el
 * precio siempre presentes; no un tipo copiado a mano.
 */
export type FilaDeLaLista = Required<
  Pick<ScopeProduct, 'sku' | 'name' | 'category' | 'brand' | 'precioHoy'>
>;

/**
 * De dónde sale la lista (plan 3, P3-D12). Reglas lista con `/simular` sobre el
 * alcance del borrador (`fuenteDeSimular`); una estrategia, que no tiene
 * acciones, con `/strategy-applications/productos` (`fuenteDelAlcance`).
 */
export interface FuenteDeLaLista {
  /** A quién se lista, como texto: es parte de la clave del pedido. */
  clave: string;
  pedir: (
    filtro: FiltroDeLaLista,
    page: number,
    limit: number
  ) => Promise<{ total: number; filas: FilaDeLaLista[] }>;
}

/** La de Reglas (27-sep): `/simular` con origen HOY, la misma cuenta que el paso 2. */
export function fuenteDeSimular(
  nombre: string,
  scope: RuleScope,
  acciones: Action[]
): FuenteDeLaLista {
  return {
    clave: JSON.stringify({ nombre, scope, acciones }),
    pedir: (filtro, page, limit) =>
      pricingRulesApi.simular(
        {
          borrador: { name: nombre, scope, actions: acciones },
          origen: 'HOY',
          filtro,
          page,
          limit,
        },
        true
      ),
  };
}

export interface SelectorDeProductosProps {
  abierto: boolean;
  titulo: string;
  descripcion?: string;
  /** Reglas: el límite del borrador (`/simular` lo pide, la lista no lo usa). */
  acciones?: Action[];
  /** Reglas: a quién se lista. */
  scope?: RuleScope;
  /** Otra fuente (plan 3, P3-D12). Si viene, manda sobre `acciones` y `scope`. */
  fuente?: FuenteDeLaLista;
  /** Los códigos que ya estaban elegidos: llegan marcados. */
  elegidos: string[];
  textoConfirmar: (n: number) => string;
  /** Los códigos elegidos y lo que se vio de ellos (nombre, categoría…). */
  onConfirmar: (skus: string[], vistos: ScopeProduct[]) => void;
  /** Spec 294 (D-R4-6): elegir uno solo, con radios (el límite en Bs). */
  unoSolo?: boolean;
  onCerrar: () => void;
}

export function SelectorDeProductos({
  abierto,
  titulo,
  descripcion,
  acciones,
  scope,
  fuente,
  elegidos,
  textoConfirmar,
  onConfirmar,
  onCerrar,
  unoSolo = false,
}: SelectorDeProductosProps) {
  const [q, setQ] = useState('');
  const [categoria, setCategoria] = useState('');
  const [marca, setMarca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [marcados, setMarcados] = useState<string[]>(elegidos);
  const [categorias, setCategorias] = useState<ScopeCategory[]>([]);
  const [marcas, setMarcas] = useState<MarcaAgrupada[]>([]);
  // Lo que se vio de cada producto, para nombrar a los elegidos de otras páginas.
  const vistos = useRef(new Map<string, ScopeProduct>());

  // Al abrir: lo elegido hasta ahora, sin filtros, en la página 1.
  useEffect(() => {
    if (!abierto) return;
    setMarcados(elegidos);
    setQ('');
    setCategoria('');
    setMarca('');
    setPagina(1);
    // Sólo al abrir: `elegidos` cambia cuando se confirma y la ventana ya se cerró.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    // Los filtros son una ayuda: si no llegan, se puede buscar igual.
    pricingRulesApi
      .listCategories(true)
      .then((c) => vivo && setCategorias(c))
      .catch(() => undefined);
    pricingRulesApi
      .listBrands(true)
      .then((m) => vivo && setMarcas(agruparMarcas(m.brands)))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [abierto]);

  const filtro = {
    ...(q.trim() && { q: q.trim() }),
    ...(categoria && { category: categoria }),
    ...(marca && { brand: marca }),
  };
  // Reglas pasa `acciones` y `scope`; una estrategia, su propia fuente (P3-D12).
  const origen = fuente ?? fuenteDeSimular(titulo, scope ?? {}, acciones ?? []);
  const pedido = usePedidoConEspera(
    abierto ? JSON.stringify({ de: origen.clave, filtro, pagina }) : null,
    () => origen.pedir(filtro, pagina, POR_PAGINA)
  );
  const datos =
    pedido.estado === 'LISTO' ? pedido.data : pedido.estado === 'CARGANDO' ? pedido.anterior : null;
  const filas = datos?.filas ?? [];
  for (const f of filas)
    vistos.current.set(f.sku, {
      sku: f.sku,
      name: f.name,
      category: f.category,
      brand: f.brand,
      precioHoy: f.precioHoy,
    });
  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const cambiarFiltro = (poner: () => void) => {
    poner();
    setPagina(1);
  };
  const marcado = (sku: string) => marcados.includes(sku);
  const alternar = (sku: string) =>
    setMarcados((m) =>
      unoSolo ? [sku] : m.includes(sku) ? m.filter((s) => s !== sku) : [...m, sku]
    );
  const paginaEntera = filas.length > 0 && filas.every((f) => marcado(f.sku));
  const alternarPagina = () =>
    setMarcados((m) =>
      paginaEntera
        ? m.filter((s) => !filas.some((f) => f.sku === s))
        : [...m, ...filas.map((f) => f.sku).filter((s) => !m.includes(s))]
    );

  const confirmar = () =>
    onConfirmar(
      marcados,
      marcados.map((s) => vistos.current.get(s)).filter((p): p is ScopeProduct => !!p)
    );

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col overflow-hidden sm:rounded-none">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descripcion && <DialogDescription>{descripcion}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <label htmlFor="selector-buscar" className="mb-1 block text-xs font-medium">
              Buscar
            </label>
            <Input
              id="selector-buscar"
              placeholder="nombre o código"
              value={q}
              onChange={(e) => cambiarFiltro(() => setQ(e.target.value))}
            />
          </div>
          <div>
            <label htmlFor="selector-categoria" className="mb-1 block text-xs font-medium">
              Categoría
            </label>
            <select
              id="selector-categoria"
              className="h-10 min-w-[200px] rounded-md border bg-background px-2 text-sm"
              value={categoria}
              onChange={(e) => cambiarFiltro(() => setCategoria(e.target.value))}
            >
              <option value="">todas</option>
              {categorias.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({num(c.count)})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="selector-marca" className="mb-1 block text-xs font-medium">
              Marca
            </label>
            <select
              id="selector-marca"
              className="h-10 min-w-[200px] rounded-md border bg-background px-2 text-sm"
              value={marca}
              onChange={(e) => cambiarFiltro(() => setMarca(e.target.value))}
            >
              <option value="">todas</option>
              {marcas.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.name} ({num(m.count)})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="flex min-h-0 flex-col rounded-md border" aria-label="Productos">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      {unoSolo && <span className="sr-only">Elegir</span>}
                      {!unoSolo && (
                        <input
                          type="checkbox"
                          aria-label="Elegir toda la página"
                          checked={paginaEntera}
                          disabled={filas.length === 0}
                          onChange={alternarPagina}
                        />
                      )}
                    </th>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2">Código</th>
                    <th className="px-3 py-2">Categoría</th>
                    <th className="px-3 py-2">Marca</th>
                    <th className="px-3 py-2 text-right">Precio hoy</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr
                      key={f.sku}
                      className="cursor-pointer border-t hover:bg-muted/40"
                      onClick={() => alternar(f.sku)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type={unoSolo ? 'radio' : 'checkbox'}
                          name={unoSolo ? 'selector-uno-solo' : undefined}
                          aria-label={`Elegir ${f.name}`}
                          checked={marcado(f.sku)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => alternar(f.sku)}
                        />
                      </td>
                      <td className="max-w-xs px-3 py-2 font-medium [overflow-wrap:anywhere]">
                        {f.name}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{f.sku}</td>
                      <td className="px-3 py-2">{f.category ?? '—'}</td>
                      <td className="px-3 py-2">{f.brand ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{precio(f.precioHoy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pedido.estado === 'ERROR' ? (
                <div className="space-y-2 p-4 text-sm">
                  <p>{mensajeDeError(pedido.error, 'No se pudo traer la lista de productos.')}</p>
                  <Button size="sm" variant="outline" onClick={pedido.reintentar}>
                    Reintentar
                  </Button>
                </div>
              ) : !datos ? (
                <p className="p-4 text-sm text-muted-foreground">Cargando productos…</p>
              ) : filas.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  Ningún producto con estos filtros.
                </p>
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-xs text-muted-foreground">
              <span>
                {num(total)} {total === 1 ? 'producto' : 'productos'} · página {num(pagina)} de{' '}
                {num(paginas)}
                {pedido.estado === 'CARGANDO' && datos && ' · cargando…'}
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  aria-label="Página anterior"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label="Siguiente página"
                  disabled={pagina >= paginas}
                  onClick={() => setPagina((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </span>
            </div>
          </section>

          <aside className="flex min-h-0 flex-col rounded-md border">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-medium">
                {unoSolo ? 'Elegido' : `Elegidos (${num(marcados.length)})`}
              </span>
              {!unoSolo && marcados.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setMarcados([])}>
                  Quitar todos
                </Button>
              )}
            </div>
            {marcados.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">
                {unoSolo
                  ? 'Elegí uno de la lista.'
                  : 'Todavía ninguno. Marcá las casillas de la lista.'}
              </p>
            ) : (
              <ul aria-label="Elegidos" className="min-h-0 flex-1 overflow-y-auto text-sm">
                {marcados.map((sku) => {
                  const p = vistos.current.get(sku);
                  return (
                    <li
                      key={sku}
                      className="flex items-center justify-between gap-2 border-b px-3 py-1.5 last:border-b-0"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">{p?.name ?? sku}</span>
                        {p && <span className="text-xs text-muted-foreground">{sku}</span>}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Quitar ${p?.name ?? sku}`}
                        onClick={() => alternar(sku)}
                      >
                        quitar
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={confirmar} disabled={unoSolo && marcados.length === 0}>
            {textoConfirmar(marcados.length)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
